import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Table, Button, Card, Alert, Loader, Badge, Dialog, Tabs } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { getApiRoot } from '@/lib/domainResolver';
import Can from '@rbac/Can';

const STATUS_VARIANT = { queued: 'warning', running: 'info', done: 'success', error: 'error', cancelled: 'default' };
const POLL_MS = 3000;

/**
 * A screenshot with an explicit failure state.
 *
 * Screenshots are served from the public Spaces CDN, and a CDN hiccup, an offline
 * client, or a privacy extension that blocks the CDN host all produce a silently broken
 * <img> — observed in practice, with the object itself provably intact server-side. Say
 * so and offer the direct link rather than rendering an empty box.
 */
function Shot({ url, label, narrow }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <Alert variant="warning" title={`Could not load the ${label} screenshot`}>
        The image is stored, but this browser could not fetch it — a CDN hiccup, no
        connection, or an extension blocking the storage host.{' '}
        <a className="underline" href={url} target="_blank" rel="noreferrer noopener">Open it directly</a>.
      </Alert>
    );
  }
  return (
    <img
      className={`${narrow ? 'mx-auto max-w-xs' : ''} w-full max-h-[70vh] object-contain object-top`}
      alt={`${label} screenshot`}
      src={url}
      onError={() => setFailed(true)}
    />
  );
}

export default function ScraperJob() {
  const { id } = useParams();
  const { fetchApi } = useAppApi();
  const [job, setJob] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null); // { page, text } — text fetched on open
  const [shot, setShot] = useState(null); // { page, viewport }
  const [cancelling, setCancelling] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [j, p] = await Promise.all([
        fetchApi('crud', { model: 'ScrapeJob', action: 'show', where: { id: Number(id) } }),
        // limit 0 is korm's documented "no limit" — without it this silently returns 10.
        fetchApi('crud', { model: 'ScrapePage', action: 'list', where: { job_id: Number(id) }, limit: 0, orderBy: { column: 'idx', direction: 'asc' } }),
      ]);
      setJob((j && j.data) || j);
      setPages(Array.isArray(p) ? p : (p && p.data) || []);
      setErr('');
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setLoading(false);
    }
  }, [fetchApi, id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (job && (job.status === 'queued' || job.status === 'running')) timer.current = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [job, load]);

  // The worker polls the row and winds itself down, so this only has to flip the status —
  // there is no signal to send and nothing to wait for. ScrapeJob.beforeUpdate is what
  // enforces that this is the only status a client may write.
  const cancel = async () => {
    setCancelling(true);
    try {
      await fetchApi('crud', { model: 'ScrapeJob', action: 'update', where: { id: Number(id) }, data: { status: 'cancelled' } });
      await load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setCancelling(false);
    }
  };

  // List responses strip `text` (see models/ScrapePage.model.js) — fetch the single row
  // when the user actually asks to read one.
  const openPreview = async (page) => {
    setPreview({ page, text: null });
    try {
      const res = await fetchApi('crud', { model: 'ScrapePage', action: 'show', where: { id: page.id } });
      const row = (res && res.data) || res;
      setPreview({ page, text: (row && row.text) || '(no text captured)' });
    } catch (ex) {
      setPreview({ page, text: `Could not load text: ${ex.message}` });
    }
  };

  // No '#'/idx column: twreact's Table renders its own serial column (showSerial defaults
  // to true), so declaring one here produced two '#' headers side by side — and they
  // disagreed, because the built-in counts 1-based row position while idx is the 0-based
  // crawl order. The built-in one is the right one to keep: it renumbers under sort and
  // filter, and it lines up with the 1-based paginator.
  const columns = [
    {
      key: 'url',
      label: 'URL',
      render: (r) => (
        <a className="text-primary-600 break-all hover:underline" href={r.url} target="_blank" rel="noreferrer noopener">{r.url}</a>
      ),
    },
    { key: 'title', label: 'Title' },
    { key: 'depth', label: 'Depth' },
    { key: 'text_chars', label: 'Chars' },
    {
      key: 'shots',
      label: 'Screenshots',
      render: (r) => (
        <div className="flex gap-2">
          {r.desktop_url ? <Button size="xs" variant="outline" onClick={() => setShot({ page: r, viewport: 'desktop' })}>Desktop</Button> : null}
          {r.mobile_url ? <Button size="xs" variant="outline" onClick={() => setShot({ page: r, viewport: 'mobile' })}>Mobile</Button> : null}
          {r.screenshot_error ? <Badge variant="error" title={r.screenshot_error}>failed</Badge> : null}
        </div>
      ),
    },
    {
      key: 'text',
      label: 'Text',
      render: (r) => (r.has_text ? <Button size="xs" variant="ghost" onClick={() => openPreview(r)}>View</Button> : <span className="opacity-50">—</span>),
    },
  ];

  if (loading) return <Loader text="Loading crawl…" />;
  if (err && !job) return <Alert variant="error" title="Could not load">{err}</Alert>;
  if (!job) return <Alert variant="error" title="Not found">No such crawl.</Alert>;

  // A plain link, not fetchApi: the browser must handle this as a file download, and the
  // JWT rides along in the httpOnly cookie.
  const downloadHref = `${getApiRoot()}/api/scrape/${job.id}/download`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to="/scraper" className="text-sm text-primary-600 hover:underline">← All crawls</Link>
          <h1 className="text-xl font-bold break-all">Crawl #{job.id}</h1>
          <a className="text-sm opacity-70 break-all hover:underline" href={job.url} target="_blank" rel="noreferrer noopener">{job.url}</a>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[job.status] || 'default'}>{job.status}</Badge>
          {job.status === 'queued' || job.status === 'running' ? (
            <Can module="scraper" action="update">
              <Button variant="outline" disabled={cancelling} onClick={cancel}>
                {cancelling ? 'Cancelling…' : 'Cancel'}
              </Button>
            </Can>
          ) : null}
          <Can module="scraper" action="export">
            {/* twreact's Button always renders a <button> — it has no `as`/`href`, so an
                anchor-style download silently does nothing. Navigating instead makes the
                browser treat the response as a file (Content-Disposition: attachment) and
                still sends the httpOnly JWT cookie the route authenticates with. */}
            <Button disabled={!pages.length} onClick={() => { window.location.href = downloadHref; }}>
              Download ZIP
            </Button>
          </Can>
        </div>
      </div>

      {job.error ? <Alert variant="error" title="Crawl failed">{job.error}</Alert> : null}
      {/* Separate from `error` on purpose: these are things the crawl carried on without,
          and rendering them under a "Crawl failed" heading would misreport a finished job. */}
      {job.warnings ? <Alert variant="warning" title="Finished with warnings">{job.warnings}</Alert> : null}

      <Card className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div><div className="opacity-70">Pages found</div><div className="text-lg font-semibold">{job.pages_found}</div></div>
          <div><div className="opacity-70">Captured</div><div className="text-lg font-semibold">{job.pages_done}</div></div>
          <div><div className="opacity-70">Failed</div><div className="text-lg font-semibold">{job.pages_failed}</div></div>
          <div><div className="opacity-70">Screenshots</div><div className="text-lg font-semibold">{job.shots_ok}{job.shots_failed ? ` / ${job.shots_failed} failed` : ''}</div></div>
        </div>
      </Card>

      <Table data={pages} columns={columns} pagination globalSearch withAction={false} />

      {shot ? (
        <Dialog isOpen onClose={() => setShot(null)} title={shot.page.title || shot.page.url} size="xl">
          <Tabs defaultIndex={shot.viewport === 'mobile' && shot.page.desktop_url ? 1 : 0}>
            <Tabs.List>
              {shot.page.desktop_url ? <Tabs.Tab>Desktop</Tabs.Tab> : null}
              {shot.page.mobile_url ? <Tabs.Tab>Mobile</Tabs.Tab> : null}
            </Tabs.List>
            <Tabs.Panels>
              {shot.page.desktop_url ? (
                <Tabs.Panel><Shot url={shot.page.desktop_url} label="desktop" /></Tabs.Panel>
              ) : null}
              {shot.page.mobile_url ? (
                <Tabs.Panel><Shot url={shot.page.mobile_url} label="mobile" narrow /></Tabs.Panel>
              ) : null}
            </Tabs.Panels>
          </Tabs>
        </Dialog>
      ) : null}

      {preview ? (
        <Dialog isOpen onClose={() => setPreview(null)} title={preview.page.title || preview.page.url} size="xl">
          {preview.text === null
            ? <Loader text="Loading text…" />
            : <pre className="whitespace-pre-wrap text-sm max-h-[70vh] overflow-auto">{preview.text}</pre>}
        </Dialog>
      ) : null}
    </div>
  );
}
